import type { ExistingOrderFingerprint, ImportedStickerRow } from './domain'

const STREET_PREFIXES = /\b(RUA|R|AVENIDA|AV|ESTRADA|EST|TRAVESSA|TV|RODOVIA|ROD)\b/g

export function normalizeText(value: unknown): string {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9 ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function normalizePhone(value: unknown): string {
  let digits = String(value ?? '').replace(/\D/g, '')
  if (digits.startsWith('00')) digits = digits.slice(2)
  if (digits.length === 10 || digits.length === 11) digits = `55${digits}`
  return digits
}

export function normalizeCep(value: unknown): string {
  return String(value ?? '').replace(/\D/g, '').slice(0, 8)
}

export function normalizeAddress(input: {
  street: unknown
  streetNumber: unknown
  neighborhood: unknown
  city: unknown
  state: unknown
  cep: unknown
}): string {
  const street = normalizeText(input.street).replace(STREET_PREFIXES, '').replace(/\s+/g, ' ').trim()
  return [
    street,
    normalizeText(input.streetNumber),
    normalizeText(input.neighborhood),
    normalizeText(input.city),
    normalizeText(input.state),
    normalizeCep(input.cep),
  ].join('|')
}

export function exactDuplicateKey(phoneNormalized: string, addressNormalized: string): string {
  return `${phoneNormalized}|${addressNormalized}`
}

export function validateImportedRow(row: ImportedStickerRow): string[] {
  const errors: string[] = []
  const addressReview = row.needsAddressReview === true
  if (normalizeText(row.recipientName).length < 2) errors.push('Nome ausente ou inválido')
  const phone = normalizePhone(row.phone)
  if (!addressReview && (phone.length < 12 || phone.length > 13)) errors.push('WhatsApp inválido')
  if (!addressReview && normalizeCep(row.cep).length !== 8) errors.push('CEP inválido')
  if (!addressReview && !normalizeText(row.street)) errors.push('Logradouro ausente')
  if (!addressReview && !normalizeText(row.streetNumber)) errors.push('Número ausente')
  if (!addressReview && !normalizeText(row.neighborhood)) errors.push('Bairro ausente')
  if (!addressReview && !normalizeText(row.city)) errors.push('Cidade ausente')
  if (!addressReview && normalizeText(row.state).length !== 2) errors.push('Estado inválido')
  if (!Number.isInteger(row.quantity) || row.quantity < 1) errors.push('Quantidade inválida')
  return errors
}

export function decideDuplicate(
  row: ImportedStickerRow,
  existing: ExistingOrderFingerprint[],
): { kind: 'NEW' | 'MERGE_EXACT' | 'REVIEW_PHONE' | 'REVIEW_ADDRESS'; existingOrderId?: string } {
  const phoneNormalized = normalizePhone(row.phone)
  const addressNormalized = normalizeAddress(row)
  const exactKey = exactDuplicateKey(phoneNormalized, addressNormalized)
  const exact = existing.find((item) => item.exactDuplicateKey === exactKey)
  if (exact) return { kind: 'MERGE_EXACT', existingOrderId: exact.id }
  const samePhone = existing.find((item) => item.phoneNormalized === phoneNormalized)
  if (samePhone) return { kind: 'REVIEW_PHONE', existingOrderId: samePhone.id }
  const sameAddress = existing.find((item) => item.addressNormalized === addressNormalized)
  if (sameAddress) return { kind: 'REVIEW_ADDRESS', existingOrderId: sameAddress.id }
  return { kind: 'NEW' }
}

export function mergeStickerModels(current: string[], incoming: string[]): string[] {
  return [...new Set([...current, ...incoming].map(normalizeText).filter(Boolean))]
}
